import classNames from 'classnames';
import {
  forwardRef,
  useCallback,
  useId,
  type ChangeEventHandler,
  type FunctionComponent,
  type JSX,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import type { TestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { PopoverMenuItemContents } from './PopoverMenu';

import * as styles from './PopoverRadio.module.less';

interface PopoverRadioGroupProps {
  title?: ReactNode;
  testId?: TestId;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

/**
 * A group of radio buttons that are used to select a single option from a list.
 * It is used to group related radio buttons together
 *
 * @example
 * <PopoverRadioGroup title="Select an option">
 *   <PopoverRadio value="1">Option 1</PopoverRadio>
 *   <PopoverRadio value="2">Option 2</PopoverRadio>
 *   <PopoverRadio value="3">Option 3</PopoverRadio>
 * </PopoverRadioGroup>
 */
export const PopoverRadioGroup: FunctionComponent<
  PropsWithChildren<PopoverRadioGroupProps>
> = ({ title, ariaLabel, ariaLabelledBy, testId, children }) => {
  return (
    <fieldset
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-testid={testId}
      className={styles.radioGroup}
    >
      {title && <legend className={styles.legend}>{title}</legend>}
      {children}
    </fieldset>
  );
};

interface PopoverRadioOptionGroupProps {
  title?: ReactNode;
  testId?: TestId;
}

/**
 * A subgroup of radio buttons within a radio group. Used to group related
 * radio buttons together under a common header
 *
 * @example
 * <PopoverRadioGroup title="Select a calendar">
 *   <PopoverRadioOptionGroup title="Google account">
 *     <PopoverRadio value="1">Calendar 1</PopoverRadio>
 *     <PopoverRadio value="2">Calendar 2</PopoverRadio>
 *   </PopoverRadioOptionGroup>
 *   <PopoverRadioOptionGroup title="Microsoft account">
 *     <PopoverRadio value="3">Calendar 3</PopoverRadio>
 *     <PopoverRadio value="4">Calendar 4</PopoverRadio>
 *   </PopoverRadioOptionGroup>
 * </PopoverRadioGroup>
 */
export const PopoverRadioOptionGroup: FunctionComponent<
  PropsWithChildren<PopoverRadioOptionGroupProps>
> = ({ title, testId, children }) => {
  return (
    <fieldset data-testid={testId} className={styles.radioSubGroup}>
      {title && <legend className={styles.legend}>{title}</legend>}
      {children}
    </fieldset>
  );
};

interface PopoverRadioProps {
  onChange: ChangeEventHandler<HTMLInputElement>;
  name: string;
  description?: ReactNode;
  rawDescription?: string;
  title?: ReactNode;
  iconBefore?: JSX.Element;
  value: string;
  disabled?: boolean;
  /**
   * Sets the initial checked state of the radio button (uncontrolled).
   * Use `checked` instead if the value may change after mount (e.g. via
   * realtime updates).
   */
  defaultChecked?: boolean;
  /**
   * Controls the checked state of the radio button (controlled). Prefer this
   * over `defaultChecked` when the selected value can change externally
   * (e.g. from a realtime/websocket update).
   */
  checked?: boolean;
  testId?: TestId;
}

/**
 * A radio button that is used to select a single option from a list.
 * PopoverRadio should always be used within a PopoverRadioGroup.
 *
 * @example
 * <PopoverRadioGroup title="Select an option">
 *   <PopoverRadio value="1">Option 1</PopoverRadio>
 *   <PopoverRadio value="2">Option 2</PopoverRadio>
 *   <PopoverRadio value="3">Option 3</PopoverRadio>
 * </PopoverRadioGroup>
 */
export const PopoverRadio = forwardRef<
  HTMLLabelElement,
  PropsWithChildren<PopoverRadioProps>
>((props, ref) => {
  const {
    onChange,
    defaultChecked,
    checked,
    value,
    disabled,
    testId,
    name,
    title,
    children,
    ...contentProps
  } = props;

  const id = useId();
  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      // Stop propagation to prevent changes from bubbling through React's
      // synthetic event system to parent components. This is necessary because
      // Popover uses React Portals, and React events in portals bubble through
      // the React component tree (not the DOM tree).
      e.stopPropagation();
      onChange(e);
    },
    [onChange],
  );

  return (
    <label
      className={classNames(styles.radio, disabled && styles.disabled)}
      ref={ref}
      htmlFor={id}
    >
      <PopoverMenuItemContents
        {...contentProps}
        align="start"
        disabled={disabled}
        iconAfter={
          <div>
            {/* oxlint-disable-next-line @atlaskit/design-system/no-readonly-or-disabled-inputs */}
            <input
              id={id}
              type="radio"
              name={name}
              data-testid={testId}
              checked={checked}
              defaultChecked={
                checked === undefined ? defaultChecked : undefined
              }
              disabled={disabled}
              value={value}
              onChange={handleChange}
              className={styles.input}
            />
            <div className={styles.icon}>
              <CheckMarkIcon
                label=""
                color={
                  disabled
                    ? token('color.text.disabled', '#091E424F')
                    : token('color.text', '#172B4D')
                }
              />
            </div>
          </div>
        }
      >
        {title || children}
      </PopoverMenuItemContents>
    </label>
  );
});
